import { GenericColumn } from './ColumnSelector';
import { DataTableColumn, DataTableView, SortByCriteria, StatusFilter } from './useDataTable';
import { ShapedLine } from '../../libs/googleSheets/models';
import { OfferLine, OptionLine, CategoryLine } from '../../libs/googleSheets/types';

/**
 * When the type `ShapedLine` has a prop `rowNumber`, it points to the exact row number in the spreadsheet (starts at 1, which is the spreadsheet header).
 * However, when we want to access the index in the error array, we need to subtract 2 from the `rowNumber` because:
 * 1. The spreadsheet header is not used as data (-1 shift), we discard it.
 * 2. The error array is 0-indexed (another -1 shift).
 * Example: an offer line in a spreadsheet that has a `rowNumber` of 37, would map to an error index of 35.
 */
export const SPREADSHEET_TO_VALIDATION_ARRAY_OFFSET = 2;

export const PAGE_SIZE = 8;

export const tableView: Record<string, DataTableView> = Object.freeze({
  OFFERS: 'offers',
  OPTIONS: 'options',
  CATEGORIES: 'categories',
});

export const statusFilter: Record<string, StatusFilter> = Object.freeze({
  ALL: 'ALL',
  ERRORS: 'ERRORS',
  UPDATES: 'UPDATES',
  ADDITIONS: 'ADDITIONS',
});

export const sortByCriteria: Record<string, SortByCriteria> = Object.freeze({
  NONE: 'NONE',
  ERRORS: 'ERRORS',
  UPDATES: 'UPDATES',
  ADDITIONS: 'ADDITIONS',
});

export const SORT_BY_OPTIONS: { value: SortByCriteria; label: string }[] = [
  { value: sortByCriteria.NONE, label: 'shared-menu.editor.importer.table-view.none' },
  { value: sortByCriteria.ERRORS, label: 'shared-menu.editor.importer.table-view.errors' },
  { value: sortByCriteria.UPDATES, label: 'shared-menu.editor.importer.table-view.updates' },
  { value: sortByCriteria.ADDITIONS, label: 'shared-menu.editor.importer.table-view.additions' },
];

export const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: statusFilter.ALL, label: 'shared-menu.editor.importer.table-view.all' },
  { value: statusFilter.ERRORS, label: 'shared-menu.editor.importer.table-view.errors' },
  { value: statusFilter.UPDATES, label: 'shared-menu.editor.importer.table-view.updates' },
  { value: statusFilter.ADDITIONS, label: 'shared-menu.editor.importer.table-view.additions' },
];

export const ALL_OFFER_COLUMNS: (GenericColumn & { value: keyof OfferLine })[] = [
  { value: 'id', label: 'shared-menu.editor.importer.table-columns.id', defaultChecked: true },
  { value: 'name', label: 'shared-menu.editor.importer.table-columns.name', defaultChecked: true },
  { value: 'price', label: 'shared-menu.editor.importer.table-columns.price', defaultChecked: true },
  { value: 'description', label: 'shared-menu.editor.importer.table-columns.description', defaultChecked: true },
  { value: 'gtin', label: 'shared-menu.editor.importer.table-columns.gtin', defaultChecked: true },
  {
    value: 'product_family_id',
    label: 'shared-menu.editor.importer.table-columns.product_family_id',
  },
  { value: 'images', label: 'shared-menu.editor.importer.table-columns.images' },
  { value: 'additives', label: 'shared-menu.editor.importer.table-columns.additives' },
  {
    value: 'alcohol_percentage',
    label: 'shared-menu.editor.importer.table-columns.alcohol_percentage',
  },
  { value: 'allergens', label: 'shared-menu.editor.importer.table-columns.allergens' },
  {
    value: 'brand_id',
    label: 'shared-menu.editor.importer.table-columns.brand_id',
  },
  {
    value: 'caffeine_content_value',
    label: 'shared-menu.editor.importer.table-columns.caffeine_content_value',
  },
  {
    value: 'caffeine_content_units',
    label: 'shared-menu.editor.importer.table-columns.caffeine_content_units',
  },
  {
    value: 'conditions_of_use_and_storage',
    label: 'shared-menu.editor.importer.table-columns.conditions_of_use_and_storage',
  },
  {
    value: 'country_of_origin',
    label: 'shared-menu.editor.importer.table-columns.country_of_origin',
  },
  { value: 'currency', label: 'shared-menu.editor.importer.table-columns.currency' },
  {
    value: 'courier_restrictions',
    label: 'shared-menu.editor.importer.table-columns.courier_restrictions',
  },
  {
    value: 'delivery_methods',
    label: 'shared-menu.editor.importer.table-columns.delivery_methods',
  },
  {
    value: 'deposit_id',
    label: 'shared-menu.editor.importer.table-columns.deposit_id',
  },
  { value: 'external_id', label: 'shared-menu.editor.importer.table-columns.external_id' },
  {
    value: 'is_bundle_offer',
    label: 'shared-menu.editor.importer.table-columns.is_bundle_offer',
  },
  {
    value: 'max_quantity_per_purchase',
    label: 'shared-menu.editor.importer.table-columns.max_quantity_per_purchase',
  },
  {
    value: 'min_quantity_per_purchase',
    label: 'shared-menu.editor.importer.table-columns.min_quantity_per_purchase',
  },
  { value: 'merchant_sku', label: 'shared-menu.editor.importer.table-columns.merchant_sku' },
  {
    value: 'number_of_units',
    label: 'shared-menu.editor.importer.table-columns.number_of_units',
  },
  { value: 'volume_in_ml', label: 'shared-menu.editor.importer.table-columns.volume_in_ml' },
  {
    value: 'weight_in_grams',
    label: 'shared-menu.editor.importer.table-columns.weight_in_grams',
  },
  {
    value: 'dietary_preferences',
    label: 'shared-menu.editor.importer.table-columns.dietary_preferences',
  },
  { value: 'discount_type', label: 'shared-menu.editor.importer.table-columns.discount_type' },
  { value: 'discount', label: 'shared-menu.editor.importer.table-columns.discount' },
  { value: 'display_unit', label: 'shared-menu.editor.importer.table-columns.display_unit' },
  {
    value: 'distributor_information',
    label: 'shared-menu.editor.importer.table-columns.distributor_information',
  },
  { value: 'enabled', label: 'shared-menu.editor.importer.table-columns.enabled' },
  // { value: 'after', label: 'shared-menu.editor.importer.table-columns.after' },
  // { value: 'before', label: 'shared-menu.editor.importer.table-columns.before' },
  { value: 'ingredients', label: 'shared-menu.editor.importer.table-columns.ingredients' },
  { value: 'inventory_mode', label: 'shared-menu.editor.importer.table-columns.inventory_mode' },
  {
    value: 'mandatory_warnings',
    label: 'shared-menu.editor.importer.table-columns.mandatory_warnings',
  },
  {
    value: 'more_information',
    label: 'shared-menu.editor.importer.table-columns.more_information',
  },
  {
    value: 'nutrition_facts',
    label: 'shared-menu.editor.importer.table-columns.nutrition_facts',
  },
  {
    value: 'option_bindings',
    label: 'shared-menu.editor.importer.table-columns.option_bindings',
  },
  {
    value: 'producer_information',
    label: 'shared-menu.editor.importer.table-columns.producer_information',
  },
  {
    value: 'sell_by_weight_type',
    label: 'shared-menu.editor.importer.table-columns.sell_by_weight_type',
  },
  {
    value: 'sell_by_weight_grams_per_step',
    label: 'shared-menu.editor.importer.table-columns.sell_by_weight_grams_per_step',
  },
  {
    value: 'user_requirements',
    label: 'shared-menu.editor.importer.table-columns.user_requirements',
  },
  { value: 'vat', label: 'shared-menu.editor.importer.table-columns.vat' },
  { value: 'vat_category', label: 'shared-menu.editor.importer.table-columns.vat_category' },

  { value: 'is_age_restricted_item', label: 'shared-menu.editor.importer.table-columns.is_age_restricted_item' },
  { value: 'age_limit', label: 'shared-menu.editor.importer.table-columns.age_limit' },
  { value: 'delivery_handshake', label: 'shared-menu.editor.importer.table-columns.delivery_handshake' },
  { value: 'is_over_the_counter', label: 'shared-menu.editor.importer.table-columns.is_over_the_counter' },
  // {
  //   value: 'weekly_availability_start',
  //   label: 'shared-menu.editor.importer.table-columns.weekly_availability_start',
  // },
  // {
  //   value: 'weekly_availability_end',
  //   label: 'shared-menu.editor.importer.table-columns.weekly_availability_end',
  // },
  // {
  //   value: 'weekly_visibility_start',
  //   label: 'shared-menu.editor.importer.table-columns.weekly_visibility_start',
  // },
  // {
  //   value: 'weekly_visibility_end',
  //   label: 'shared-menu.editor.importer.table-columns.weekly_visibility_end',
  // },
];

export const ALL_OPTION_COLUMNS: (GenericColumn & { value: keyof OptionLine })[] = [
  { value: 'id', label: 'shared-menu.editor.importer.table-columns.id', defaultChecked: true },
  { value: 'name', label: 'shared-menu.editor.importer.table-columns.name', defaultChecked: true },
  { value: 'price', label: 'shared-menu.editor.importer.table-columns.price', defaultChecked: true },
  { value: 'description', label: 'shared-menu.editor.importer.table-columns.description', defaultChecked: true },
  {
    value: 'option_value_id',
    label: 'shared-menu.editor.importer.table-columns.option_value_id',
    defaultChecked: true,
  },
  {
    value: 'option_value_name',
    label: 'shared-menu.editor.importer.table-columns.option_value_name',
    defaultChecked: true,
  },
  { value: 'default', label: 'shared-menu.editor.importer.table-columns.default', defaultChecked: true },
  { value: 'type', label: 'shared-menu.editor.importer.table-columns.type', defaultChecked: true },
  { value: 'max', label: 'shared-menu.editor.importer.table-columns.max' },
  { value: 'min', label: 'shared-menu.editor.importer.table-columns.min' },
  { value: 'additives', label: 'shared-menu.editor.importer.table-columns.additives' },
  {
    value: 'alcohol_permille',
    label: 'shared-menu.editor.importer.table-columns.alcohol_permille',
  },
  { value: 'allergens', label: 'shared-menu.editor.importer.table-columns.allergens' },
  {
    value: 'conditions_of_use_and_storage',
    label: 'shared-menu.editor.importer.table-columns.conditions_of_use_and_storage',
  },
  {
    value: 'country_of_origin',
    label: 'shared-menu.editor.importer.table-columns.country_of_origin',
  },
  { value: 'currency', label: 'shared-menu.editor.importer.table-columns.currency' },
  {
    value: 'dietary_preferences',
    label: 'shared-menu.editor.importer.table-columns.dietary_preferences',
  },
  { value: 'deposit_id', label: 'shared-menu.editor.importer.table-columns.deposit_id' },
  { value: 'display_unit', label: 'shared-menu.editor.importer.table-columns.display_unit' },
  {
    value: 'distributor_information',
    label: 'shared-menu.editor.importer.table-columns.distributor_information',
  },
  { value: 'external_id', label: 'shared-menu.editor.importer.table-columns.external_id' },
  { value: 'option_value_external_id', label: 'shared-menu.editor.importer.table-columns.option_value_external_id' },
  { value: 'gtin', label: 'shared-menu.editor.importer.table-columns.gtin' },
  { value: 'ingredients', label: 'shared-menu.editor.importer.table-columns.ingredients' },
  {
    value: 'mandatory_warnings',
    label: 'shared-menu.editor.importer.table-columns.mandatory_warnings',
  },
  { value: 'merchant_sku', label: 'shared-menu.editor.importer.table-columns.merchant_sku' },
  {
    value: 'more_information',
    label: 'shared-menu.editor.importer.table-columns.more_information',
  },

  {
    value: 'nutrition_facts',
    label: 'shared-menu.editor.importer.table-columns.nutrition_facts',
  },
  {
    value: 'nutrition_values',
    label: 'shared-menu.editor.importer.table-columns.nutrition_values',
  },
  { value: 'numberOfUnits', label: 'shared-menu.editor.importer.table-columns.number_of_units' },
  {
    value: 'producer_information',
    label: 'shared-menu.editor.importer.table-columns.producer_information',
  },
  { value: 'vat', label: 'shared-menu.editor.importer.table-columns.vat' },
  { value: 'vat_category', label: 'shared-menu.editor.importer.table-columns.vat_category' },
  {
    value: 'visible_to_courier',
    label: 'shared-menu.editor.importer.table-columns.visible_to_courier',
  },
  {
    value: 'visible_to_merchant_app',
    label: 'shared-menu.editor.importer.table-columns.visible_to_merchant_app',
  },
  { value: 'volume_in_ml', label: 'shared-menu.editor.importer.table-columns.volume_in_ml' },
  {
    value: 'weight_in_grams',
    label: 'shared-menu.editor.importer.table-columns.weight_in_grams',
  },
];

export const ALL_CATEGORY_COLUMNS: (GenericColumn & { value: keyof CategoryLine })[] = [
  { value: 'id', label: 'shared-menu.editor.importer.table-columns.id', defaultChecked: true },
  { value: 'name', label: 'shared-menu.editor.importer.table-columns.name', defaultChecked: true },
  { value: 'description', label: 'shared-menu.editor.importer.table-columns.description', defaultChecked: true },
  { value: 'subcategories', label: 'shared-menu.editor.importer.table-columns.subcategories', defaultChecked: true },
  { value: 'offers', label: 'shared-menu.editor.importer.table-columns.offers', defaultChecked: true },
];

const prependRowNumberAndStatusColumns = <T extends Partial<ShapedLine>>(
  originalColumns: DataTableColumn<T>[],
): DataTableColumn<T>[] => [
  {
    value: 'rowNumber',
    label: 'shared-menu.editor.importer.table-columns.rowNumber',
    defaultChecked: true,
    disabled: true,
  },
  { value: 'status', label: 'shared-menu.editor.importer.table-columns.status', defaultChecked: true, disabled: true },
  ...originalColumns,
];

export const ALL_DATA_TABLE_OFFER_COLUMNS = prependRowNumberAndStatusColumns(ALL_OFFER_COLUMNS);
export const ALL_DATA_TABLE_OPTION_COLUMNS = prependRowNumberAndStatusColumns(ALL_OPTION_COLUMNS);
export const ALL_DATA_TABLE_CATEGORY_COLUMNS = prependRowNumberAndStatusColumns(ALL_CATEGORY_COLUMNS);

export const ALL_DATA_TABLE_COLUMNS_OPTIONS = {
  [tableView.OFFERS]: ALL_DATA_TABLE_OFFER_COLUMNS,
  [tableView.OPTIONS]: ALL_DATA_TABLE_OPTION_COLUMNS,
  [tableView.CATEGORIES]: ALL_DATA_TABLE_CATEGORY_COLUMNS,
} as Record<DataTableView, DataTableColumn<Partial<ShapedLine>>[]>;

export const ALL_COLUMNS_OPTIONS = {
  [tableView.OFFERS]: ALL_OFFER_COLUMNS,
  [tableView.OPTIONS]: ALL_OPTION_COLUMNS,
  [tableView.CATEGORIES]: ALL_CATEGORY_COLUMNS,
} as Record<DataTableView, GenericColumn[]>;
